/**
 * Shared BAA field metadata (grok-baa-template-builder-2026-08-23).
 *
 * Single source of truth for:
 *  - FIELD_COPY: plain-English question titles/help per standard-template slug
 *    (moved verbatim out of BaaGuidedWizard.vue so the send wizard and the
 *    standard-template builder can never drift apart);
 *  - FIELD_PREFILL + resolveFieldValue: the default clause language a free-text
 *    fill-in opens with, so the builder never shows an empty box (Round 9);
 *  - applyPreviewTriggers: reproduces the server's conditional show/hide in the
 *    live preview, so a free-text answer is visible where it will be signed,
 *    and isJunkAnswer: answers that are present but say nothing (Round 10);
 *  - autoSizePreviewFields / previewInputSize: size a revealed fill-in to its
 *    own content so the clause is readable, not a 119px peephole (Round 11);
 *  - mountInlineEditors: a contenteditable shim so a sentence-continuation
 *    fill-in WRAPS inline and shows its whole clause (Round 12);
 *  - bindPreviewEditing: every other preview control syncs back to the field
 *    state too, so nothing accepts keystrokes it throws away (Round 13), and
 *    applyPreviewReadOnly locks the ones with no editable home (Round 13b);
 *  - DEPENDENT_FIELDS: which question is revealed by which controlling answer
 *    (verified against database/seeders/BAASeeder.php — see the wizard's
 *    original notes);
 *  - THEMES: the grouped-question layout for the side-by-side builder
 *    (mirrors the Dev Policy split builder's questions-left/document-right);
 *  - vendor-specific slug detection: fields that are answered per-send (the
 *    vendor's name, the agreement dates), never at the template level.
 *
 * Plain module (no Vue) so the grouping/partition rules are unit-testable
 * with bare node — see tests/js/templateThemes.test.mjs.
 */

var FIELD_COPY = {
    as_of_date: {
        title: 'As of what date is this Business Associate Agreement made?',
        help: "This date appears in the agreement's opening paragraph as the date the Agreement is made.",
    },
    business_associate: {
        title: "What is the Business Associate's legal name?",
        help: "This name identifies the Business Associate everywhere the agreement uses that defined term, including the definitions section.",
    },
    stricter_timeframe: {
        title: 'Do you want the Business Associate to report a potential breach to the Covered Entity sooner than the standard requirement?',
        help: 'Choose No to leave the standard breach notification requirement in place, or pick a 3 or 5 day deadline to add a stricter reporting sentence.',
    },
    ba_handle_breach_notification: {
        title: 'Should the Business Associate handle breach notification to individuals, the HHS Office for Civil Rights, and the media, on behalf of the Covered Entity?',
        help: 'Choosing Yes adds a sentence assigning that notification duty to the Business Associate instead of leaving it with the Covered Entity.',
    },
    ba_additional_specificity_access: {
        title: "Do you want to spell out exactly how the Business Associate will respond to an individual's direct request for access to their records?",
        help: 'Choosing Yes lets you describe the specific process, timing, and whether the request gets forwarded to the Covered Entity, choosing No leaves only the general access obligation.',
    },
    ba_additional_specificity_access_yes: {
        title: "Describe how the Business Associate will handle a direct individual request for access to their records.",
        help: 'This text is added to the clause about making protected health information available to individuals.',
    },
    ba_additional_specificity_amendment: {
        title: "Do you want to spell out exactly how the Business Associate will respond to an individual's direct request to amend their records?",
        help: 'Choosing Yes lets you describe the specific process, timing, and whether the request gets forwarded to the Covered Entity, choosing No leaves only the general amendment obligation.',
    },
    ba_additional_specificity_amendment_yes: {
        title: "Describe how the Business Associate will handle a direct individual request to amend their records.",
        help: 'This text is added to the clause about amending protected health information.',
    },
    ba_additional_specificity_accounting: {
        title: "Do you want to spell out exactly how the Business Associate will respond to an individual's direct request for an accounting of disclosures?",
        help: 'Choosing Yes lets you describe the specific process, timing, and whether the request gets forwarded to the Covered Entity, choosing No leaves only the general accounting obligation.',
    },
    ba_additional_specificity_accounting_yes: {
        title: "Describe how the Business Associate will handle a direct individual request for an accounting of disclosures.",
        help: 'This text is added to the clause about accounting for disclosures of protected health information.',
    },
    ba_how_use_phi: {
        title: 'How may the Business Associate use or disclose protected health information under this agreement?',
        help: 'Choose the first option to limit use strictly to performing the Service, or the second to list specific permissible purposes yourself.',
    },
    ba_how_use_phi_following_permissible_list: {
        title: 'List the specific permissible purposes for which the Business Associate may use or disclose protected health information.',
        help: "This list is added to the clause describing the Business Associate's permitted uses and disclosures.",
    },
    ba_how_wil_disclose_phi: {
        title: "Should the Business Associate follow the Covered Entity's existing minimum necessary policies, or a specific set of minimum necessary requirements you write here?",
        help: "Choose the first option to rely on the Covered Entity's standing policies and procedures, or the second to spell out custom requirements.",
    },
    ba_how_wil_disclose_phi_following_requirements: {
        title: 'Describe the specific minimum necessary provisions the Business Associate must follow.',
        help: "This text is added to the agreement's minimum necessary requirements clause.",
    },
    ba_can_use_phi: {
        title: 'Which additional uses of protected health information should the Business Associate be allowed, for its own management, legal responsibilities, or data aggregation services?',
        help: 'Select all that apply. Each selection adds its own permitted-use clause to the agreement.',
    },
    ba_provisions_for_ca: {
        title: "Which notice obligations should the Covered Entity have to the Business Associate about its own privacy practices and restrictions?",
        help: 'Select all that apply. Each selection adds a clause requiring the Covered Entity to notify the Business Associate of that kind of change.',
    },
    ba_permissible_requests: {
        title: 'Which limits or allowances should apply to what the Covered Entity may ask the Business Associate to do with protected health information?',
        help: 'Select all that apply. Each selection adds its own clause under Permissible Requests by Covered Entity.',
    },
    effective_date: {
        title: "What date should this agreement's term become effective?",
        help: "This is the start of the agreement's term, in the Term and Termination section.",
    },
    termination_date_yes_no: {
        title: 'Should this agreement terminate on a specific date or event, in addition to termination for cause?',
        help: 'Choosing Yes lets you enter that date or event, choosing No leaves termination for cause as the only stated end point.',
    },
    termination_date: {
        title: 'What is the specific termination date or event for this agreement?',
        help: 'This date or event is added to the Term and Termination section alongside termination for cause.',
    },
    ba_add_termination: {
        title: 'Should the Business Associate get a chance to fix a violation before the Covered Entity can terminate for cause?',
        help: 'Choosing Yes adds a condition that the Covered Entity may only terminate for cause after the Business Associate fails to cure the violation within the time the Covered Entity specifies.',
    },
    ba_destroy_phi: {
        title: 'Should the Business Associate return or destroy all protected health information when this agreement ends?',
        help: 'Choosing Yes adds the standard clause requiring the Business Associate to return or destroy the information and retain no copies.',
    },
    ba_authorized_phi: {
        title: 'Should the Business Associate be allowed to keep protected health information after termination for its own management, administration, or legal responsibilities?',
        help: 'Choosing Yes adds a clause letting the Business Associate retain only what it needs for those purposes, with continued safeguards, and return or destroy the rest.',
    },
    ba_transmit_to_ba: {
        title: 'Should the Business Associate transmit protected health information to another Business Associate of the Covered Entity when this agreement ends?',
        help: 'Choosing Yes adds a clause requiring that hand-off as part of the termination obligations.',
    },
    ba_add_terms: {
        title: "Should the Business Associate be required to ensure its subcontractors destroy protected health information they created, received, or maintained?",
        help: 'Choosing Yes adds a clause requiring the Business Associate to obtain or ensure that destruction from its subcontractors.',
    },
}

/**
 * Round 9 (Joe req 2026-09-01): default clause language for the standard
 * template's FREE-TEXT fill-ins.
 *
 * The Medcurity master template's "Describe..." questions are plain text /
 * textarea merge fields, so the builder rendered them as empty boxes whose only
 * content was a placeholder repeating the question. An admin building their
 * standard BAA had nothing to react to and nothing to accept.
 *
 * These are DEFAULTS, offered as real editable values so the admin can accept
 * or edit them - never placeholders, and never forced. They are deliberately
 * conservative and plainly worded: the Business Associate forwards individual
 * requests to the Covered Entity rather than answering them itself, uses are
 * limited to performing the Service, and every stated window is a common,
 * round number (five business days to forward a request, thirty days to
 * cooperate) so the same number reads consistently across the agreement.
 *
 * Only free-text fill-ins belong here. Names, dates and entity merge fields are
 * record data answered per send (see isVendorSpecificSlug), and select /
 * checkbox questions already ship their own option text.
 *
 * Two of these values are sentence CONTINUATIONS: the template renders a fixed
 * lead-in string immediately before them, so they start in lower case and must
 * read on from that lead-in. The lead-in is quoted above each one.
 */
var FIELD_PREFILL = {
    // Standalone sentences added to the individual-rights clauses.
    ba_additional_specificity_access_yes:
        "The Business Associate will forward any request for access to protected health information that it receives directly from an individual to the Covered Entity within five business days, and will not respond to the request directly unless the Covered Entity directs otherwise. The Business Associate will give the Covered Entity the protected health information it maintains in the designated record set, in the form and format the Covered Entity asks for, within thirty days of that request so the Covered Entity can respond to the individual on time.",
    ba_additional_specificity_amendment_yes:
        "The Business Associate will forward any request to amend protected health information that it receives directly from an individual to the Covered Entity within five business days, and will not act on the request directly unless the Covered Entity directs otherwise. The Business Associate will incorporate any amendment the Covered Entity approves into the protected health information it maintains in the designated record set within thirty days of being notified, and will confirm to the Covered Entity once the amendment has been made.",
    ba_additional_specificity_accounting_yes:
        "The Business Associate will forward any request for an accounting of disclosures that it receives directly from an individual to the Covered Entity within five business days, and will not respond to the request directly unless the Covered Entity directs otherwise. The Business Associate will keep a record of the disclosures it makes that must be included in an accounting and will provide that record to the Covered Entity within thirty days of the Covered Entity's request.",

    // Continues: "...the Parties agree that the following are permissible
    // purposes: Business Associate may only use or disclose protected health
    // information "
    ba_how_use_phi_following_permissible_list:
        "as necessary to perform the Service described in the underlying services agreement, to carry out the Business Associate's obligations under that agreement, and as required by law.",

    // Continues: "Business Associate agrees to make uses and disclosures and
    // requests for protected health information consistent "
    ba_how_wil_disclose_phi_following_requirements:
        "with the Covered Entity's minimum necessary policies and procedures, limiting each use, disclosure, and request to the least amount of protected health information needed to accomplish the intended purpose, and using a limited data set wherever a limited data set will serve that purpose.",
}

/** The default clause language for a slug, or '' when it has none. */
function prefillFor(slug) {
    var v = FIELD_PREFILL[String(slug || '')]

    return typeof v === 'string' ? v : ''
}

/**
 * The single rule deciding what a builder question opens with. Kept here, as a
 * pure function, so it is unit-testable with bare node (see
 * tests/js/templateFieldDefaults.test.mjs) rather than only reachable through a
 * mounted component.
 *
 * Precedence, highest first:
 *   1. the template's SAVED answer - an admin's own words always win, and an
 *      existing saved template is never rewritten by a later default;
 *   2. a value the server already rendered into the control;
 *   3. the standard default language above, for free-text questions only.
 *
 * A question with options (select / checkbox) never takes a default: its
 * answer set is the template's own option list.
 */
function resolveFieldValue(opts) {
    var o = opts || {}
    if (o.hasSaved) return o.savedValue

    var dom = o.domValue == null ? '' : String(o.domValue)
    if (dom.trim() !== '') return dom
    if (o.hasOptions) return dom

    return prefillFor(o.slug) || dom
}

/**
 * Round 10 (Joe req 2026-09-01): make the live preview tell the truth.
 *
 * The server renders a template's conditional children hidden:
 * PolicyController::generateInput() generates every trigger target with
 * $display = false, which components/policy-input.blade.php emits as
 * style="display: none !important;". The ONLY thing that ever reveals one is
 * checktrigger(), a jQuery helper defined inline in
 * resources/views/company_baa/edit.blade.php and bound through the option's
 * data-triggers attribute.
 *
 * Two consequences, both proven live:
 *   1. The standard-template builder page (company_baa/templates/builder)
 *      never defines checktrigger() at all, so its preview could not reveal a
 *      conditional block under any circumstance.
 *   2. Both builders set control values programmatically (el.value = ...),
 *      which fires no change event, so even where checktrigger() exists it
 *      never runs for an answer typed in the question pane.
 *
 * Net effect: answering "Yes" to an individual-rights question and typing into
 * the free-text box changed nothing visible in the preview. The answer was
 * NOT lost - it posts, it persists in template_answers, and it compiles into
 * the generated agreement - but the preview was blind to it, so what you saw
 * was not what would be signed.
 *
 * This reproduces the server's own show/hide contract, in the component, off
 * the rendered DOM rather than off any hard-coded slug list, so it covers any
 * template's trigger graph and cannot drift from what the server emitted:
 * for every <select> in the preview, the <option data-triggers="id ..."> whose
 * value is currently selected reveals its targets and every other option's
 * targets are hidden.
 *
 * Show/hide mirror checktrigger() exactly: hiding re-applies
 * display:none !important, showing removes the declaration entirely (including
 * its importance) so the element falls back to its natural display.
 */
/**
 * Round 13b (verification fix 2026-09-01): record-data merge fields.
 *
 * Round 13 bound every preview control, but two-way sync only reaches as far as
 * the component's field state, and for RECORD DATA that state is not the home.
 * Measured live on the wizard's Vendor step: typing into the preview's Business
 * Associate Name updated both preview occurrences (so binding and mirroring
 * worked) while the builder field stayed put, and the next keystroke in the
 * builder field silently overwrote the preview edit.
 *
 * Why: business_associate is DERIVED. BaaGuidedWizard holds the vendor name in
 * its own `baCompany` answer and pushes it into the merge field through
 * applyAutoBindings()/syncAutoBindings(). Writing f.value therefore wrote to a
 * copy, and the next sync overwrote it. In BaaTemplateBuilder these fields have
 * no home at all: they render as a read-only "Asked when you send" list and are
 * excluded from the answers[] the form posts.
 *
 * So a control is two-way when the component has somewhere real to put the
 * value, and READ-ONLY when it does not. Never fake-editable.
 */

/**
 * Mark controls the component cannot accept edits for as genuinely read-only:
 * `readonly` (which still submits, unlike `disabled`), announced via
 * aria-readonly, and given a class the preview panes style so it is visibly not
 * an editable spot. Re-runs safely: a field can become editable again when the
 * model changes underneath it.
 */
function applyPreviewReadOnly(root, isReadOnly) {
    if (!root || typeof root.querySelectorAll !== 'function') return
    if (typeof isReadOnly !== 'function') return

    Array.prototype.forEach.call(root.querySelectorAll('input, textarea'), function (el) {
        if (!el.getAttribute || !el.setAttribute) return
        var slug = el.getAttribute('data-slug') || el.getAttribute('data-original-slug') || ''
        if (!slug) return

        var lock = !!isReadOnly(slug)
        if (lock) {
            el.setAttribute('readonly', 'readonly')
            el.setAttribute('aria-readonly', 'true')
            if (el.classList && el.classList.add) el.classList.add('mc-preview-readonly')
        } else if (el.getAttribute('readonly') !== null) {
            el.removeAttribute('readonly')
            el.removeAttribute('aria-readonly')
            if (el.classList && el.classList.remove) el.classList.remove('mc-preview-readonly')
        }
    })
}

/**
 * Round 13 (Joe req 2026-09-01): no control may discard what the user types.
 *
 * The preview pane renders real form controls, but nothing was ever listening
 * to them. Round 12 gave the two sentence-continuation fill-ins a shim that
 * syncs both ways; every other control was still fake-editable. In the builder
 * the posted answer comes from the component's own field state, so a preview
 * edit reached nothing and was overwritten by the next reflect (measured live:
 * the builder box held 550 characters while the preview held 735). In the send
 * wizard the preview controls DO post, but the component's field state did not
 * know, so a later reflect could still silently revert them.
 *
 * The audit found no listener of any kind on any preview control, so this is
 * the whole class of bug, not one field: textareas, selects, checkbox groups
 * and the record-data text inputs were all affected. bindPreviewEditing wires
 * every one of them.
 */

/**
 * Whitespace normalisation for MULTI-LINE content.
 *
 * The three individual-rights answers are several sentences long and their line
 * structure is the author's, so unlike normalizeInlineText this deliberately
 * keeps newlines, tabs and internal spacing. Only characters that would be
 * invisible-but-different are folded: a non-breaking space pasted from Word
 * looks identical to a space but compares differently, which is how "identical"
 * text fails an equality check later.
 */
function normalizePlainText(text) {
    if (text === null || text === undefined) return ''

    return String(text).replace(/[\u00a0\u2028\u2029]/g, ' ')
}

/**
 * Write a field's value into every DOM element backing it EXCEPT the one the
 * edit came from. Skipping the source is what stops a round trip from resetting
 * the caret of the control being typed in.
 */
function mirrorFieldValue(field, value, skipEl) {
    if (!field || !field.els) return

    if (field.checkbox) {
        var vals = Array.isArray(value) ? value : []
        field.els.forEach(function (e) {
            if (e === skipEl) return
            e.checked = vals.indexOf(e.value) !== -1
        })

        return
    }

    field.els.forEach(function (e) {
        if (e === skipEl) return
        e.value = value
    })
}

/** The value a control currently represents, in the shape the field expects. */
function readControlValue(root, el) {
    var tag = String(el.tagName || '').toLowerCase()
    var type = (el.getAttribute ? (el.getAttribute('type') || '') : '').toLowerCase()

    if (tag === 'input' && type === 'checkbox') {
        // A checkbox fill-in is a GROUP sharing one slug; the value is every
        // box currently ticked, in document order.
        var slug = el.getAttribute('data-slug') || el.getAttribute('data-original-slug') || ''
        var checked = []
        Array.prototype.forEach.call(root.querySelectorAll('input[type="checkbox"]'), function (box) {
            var boxSlug = box.getAttribute('data-slug') || box.getAttribute('data-original-slug') || ''
            if (boxSlug === slug && box.checked) checked.push(box.value)
        })

        return checked
    }

    if (tag === 'textarea') return normalizePlainText(el.value)
    if (tag === 'select') return el.value

    return normalizeInlineText(el.value)
}

/**
 * Make every control in a rendered preview genuinely editable: an edit writes
 * through to its siblings and is reported to the host component, which puts it
 * in the field state that drives `answers[slug]` and the answered counter.
 *
 * Idempotent, so it is safe to call after every extract/reveal. A control that
 * already carries a Round 12 shim is skipped: the shim owns that input.
 *
 * `options.onInput(slug, value, sourceElement)` is the host callback;
 * `options.getField(slug)` lets this mirror into the field's other elements
 * without the host having to re-implement the skip-the-source rule.
 */
function bindPreviewEditing(root, options) {
    if (!root || typeof root.querySelectorAll !== 'function') return []

    var opts = options || {}
    var bound = []

    Array.prototype.forEach.call(root.querySelectorAll('input, textarea, select'), function (el) {
        if (!el.getAttribute || !el.addEventListener) return
        if (el.getAttribute('data-mc-bound') === '1') return
        // The shim owns its input; binding it here too would double-fire.
        if (el.getAttribute('data-mc-shimmed') === '1') return
        // Round 13b: a control with no editable home is read-only, not bound.
        if (el.getAttribute('readonly') !== null) return

        var slug = el.getAttribute('data-slug') || el.getAttribute('data-original-slug') || ''
        if (!slug) return

        var tag = String(el.tagName || '').toLowerCase()
        el.setAttribute('data-mc-bound', '1')

        var handler = function () {
            var value = readControlValue(root, el)

            // A textarea grows as it is typed in, not only when text arrives
            // from the question pane.
            if (tag === 'textarea') autoSizeTextarea(el)

            if (typeof opts.getField === 'function') {
                mirrorFieldValue(opts.getField(slug), value, el)
            }
            if (typeof opts.onInput === 'function') opts.onInput(slug, value, el)
        }

        // `input` covers typing; `change` covers select and checkbox, which do
        // not fire `input` consistently across browsers.
        el.addEventListener('input', handler)
        el.addEventListener('change', handler)

        bound.push(el)
    })

    return bound
}

/**
 * Round 12 (Joe req 2026-09-01): let a sentence-continuation fill-in WRAP.
 *
 * Round 11 freed these controls from Bootstrap's .w-25 (about 119px) and sized
 * them to their content, but they are single-line <input> elements and an input
 * cannot wrap, so a long clause still clipped: permissible-purposes measured
 * scrollWidth 1261 against clientWidth 477 (about 38% visible), minimum
 * necessary 2012 against 477 (about 24%), and the rendered clause ended
 * mid-word.
 *
 * The fix is a contenteditable shim. The real <input> stays in the DOM and
 * remains the submit-path source of truth - it is only made visually hidden, so
 * `answers[slug]` posting, the wizard's own form post, reflect()/sync() and the
 * "N of M answered" counter are all completely unchanged. A <span
 * contenteditable> is inserted next to it, mirrors its value, and being an
 * inline span it wraps across as many lines as the sentence needs while reading
 * as one continuous sentence with the lead-in text.
 *
 * The three individual-rights fields are textareas that already auto-grow and
 * show their full text; they are deliberately NOT shimmed.
 */

/**
 * Slugs whose answer continues a sentence that is already running in the
 * document, so the control has to flow and wrap inline with the prose.
 */
var INLINE_WRAP_SLUGS = [
    'ba_how_use_phi_following_permissible_list',
    'ba_how_wil_disclose_phi_following_requirements',
]

/** Below this many characters an ordinary input shows its whole value anyway. */
var INLINE_WRAP_LENGTH = 80

/**
 * Should this control be shimmed? The declared continuation slugs always are.
 * Any other single-line fill-in is shimmed only when its own content is long
 * enough to clip, so a future template with a long continuation answer is
 * covered without naming it here, while short fields (names, dates) keep their
 * ordinary input.
 *
 * Pure: takes the slug and current value, so it is unit-testable.
 */
function needsInlineEditor(slug, value) {
    if (INLINE_WRAP_SLUGS.indexOf(String(slug || '')) !== -1) return true

    return String(value === null || value === undefined ? '' : value).length > INLINE_WRAP_LENGTH
}

/**
 * Plain text, always. The shim renders into a legal document, so nothing that
 * comes out of it may carry markup or line structure: newlines, tabs and
 * non-breaking spaces collapse to ordinary spaces and runs of whitespace are
 * squeezed. Deliberately NOT trimmed - trimming while someone is typing would
 * eat the space they just pressed.
 */
function normalizeInlineText(text) {
    if (text === null || text === undefined) return ''

    return String(text)
        .replace(/[\u00a0\u2028\u2029]/g, ' ')
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/ {2,}/g, ' ')
}

/**
 * The attributes the shim carries. Separated out so the accessibility wiring is
 * assertable without a DOM.
 *
 * The shim is the thing a keyboard reaches: it sits immediately after its input
 * in document order, so tab order still matches reading order, and it is
 * labelled by the SAME question text the input carries (data-label, which is the
 * question the builder asks in its left pane). The input itself is taken out of
 * the tab order and hidden from assistive tech so it is not announced twice.
 */
function inlineEditorAttrs(input) {
    var get = function (name) {
        return (input && input.getAttribute ? input.getAttribute(name) : null) || ''
    }
    var label = get('data-label') || get('placeholder') || 'Answer'

    return {
        'class': 'mc-inline-edit',
        contenteditable: 'true',
        role: 'textbox',
        'aria-multiline': 'false',
        'aria-label': label,
        tabindex: '0',
        spellcheck: 'true',
        'data-mc-shim-for': get('id'),
        'data-placeholder': get('placeholder') || label,
    }
}

/**
 * Give every qualifying fill-in in a rendered preview an inline editable shim.
 *
 * Idempotent: an already-shimmed input is skipped, so this is safe to call after
 * every reveal. `options.document` is the document to create nodes with (the
 * tests pass a stand-in); `options.onInput(slug, value, input)` is called on
 * every edit so the host component can push the value into its own field state
 * and run its normal sync, which is what keeps builder posting working.
 */
function mountInlineEditors(root, options) {
    if (!root || typeof root.querySelectorAll !== 'function') return []

    var opts = options || {}
    var doc = opts.document || (typeof document !== 'undefined' ? document : null)
    if (!doc || typeof doc.createElement !== 'function') return []

    var mounted = []

    Array.prototype.forEach.call(root.querySelectorAll('input'), function (input) {
        if (!input.getAttribute) return
        var type = (input.getAttribute('type') || 'text').toLowerCase()
        if (type !== 'text') return
        if (input.getAttribute('data-mc-shimmed') === '1') return

        var slug = input.getAttribute('data-slug') || input.getAttribute('data-original-slug') || ''
        if (!needsInlineEditor(slug, input.value)) return

        var attrs = inlineEditorAttrs(input)
        var shim = doc.createElement('span')
        Object.keys(attrs).forEach(function (name) { shim.setAttribute(name, attrs[name]) })
        shim.textContent = normalizeInlineText(input.value)

        // Keep the input in the DOM and in the form; hide it visually only.
        // NOT display:none - applyPreviewTriggers() owns the inline `display` on
        // these exact ids, and would fight any display we set here.
        input.setAttribute('data-mc-shimmed', '1')
        input.setAttribute('tabindex', '-1')
        input.setAttribute('aria-hidden', 'true')
        if (input.classList && input.classList.add) input.classList.add('mc-shim-source')

        if (input.parentNode && input.parentNode.insertBefore) {
            input.parentNode.insertBefore(shim, input.nextSibling || null)
        }

        var push = function () {
            var text = normalizeInlineText(shim.textContent)
            input.value = text
            if (typeof opts.onInput === 'function') opts.onInput(slug, text, input)
        }

        if (shim.addEventListener) {
            shim.addEventListener('input', push)
            shim.addEventListener('blur', push)

            // Paste and drop are the two ways rich markup gets in. Take the
            // plain-text flavour only and insert it as text at the caret.
            var insertPlain = function (event, transfer) {
                if (event.preventDefault) event.preventDefault()
                var text = ''
                try { text = transfer ? transfer.getData('text/plain') : '' } catch (e) { text = '' }
                text = normalizeInlineText(text)
                var inserted = false
                if (doc.execCommand) {
                    try { inserted = doc.execCommand('insertText', false, text) } catch (e) { inserted = false }
                }
                if (!inserted) shim.textContent = normalizeInlineText(shim.textContent + text)
                push()
            }
            shim.addEventListener('paste', function (e) { insertPlain(e, e.clipboardData) })
            shim.addEventListener('drop', function (e) { insertPlain(e, e.dataTransfer) })

            // These are single sentences: Enter commits rather than inserting a
            // <br>/<div>, which is also what stops the browser building markup
            // inside the shim.
            shim.addEventListener('keydown', function (e) {
                if (e.key === 'Enter') {
                    if (e.preventDefault) e.preventDefault()
                    if (shim.blur) shim.blur()
                }
            })
        }

        mounted.push(shim)
    })

    return mounted
}

/**
 * Push each input's value back into its shim, so an edit made in the question
 * pane shows up in the document. The shim the person is currently typing in is
 * skipped: rewriting its text under the caret would move the caret to the start.
 */
function syncInlineEditors(root, activeElement) {
    if (!root || typeof root.querySelectorAll !== 'function') return

    var active = activeElement
    if (active === undefined && typeof document !== 'undefined') active = document.activeElement

    Array.prototype.forEach.call(root.querySelectorAll('[data-mc-shim-for]'), function (shim) {
        if (shim === active) return
        var id = shim.getAttribute('data-mc-shim-for')
        var input = byId(root, id)[0]
        if (!input) return
        var text = normalizeInlineText(input.value)
        if (shim.textContent !== text) shim.textContent = text
    })
}

function byId(root, id) {
    return root.querySelectorAll('[id="' + String(id).replace(/"/g, '\\"') + '"]')
}

/**
 * The ids one option reveals.
 *
 * components/policy-input.blade.php builds this attribute by concatenating one
 * id per matching trigger with NO separator, so an option with two triggers
 * (the "permissible purposes" and "minimum necessary requirements" branches
 * each reveal a fixed lead-in string AND a free-text control) emits a single
 * mashed token that matches no element. That format cannot simply be changed:
 * the list-repeater scripts in that same file, in company_policy/editor and in
 * the Baa/Policy MedcurityInputs components all do string surgery on this
 * attribute assuming exactly one id.
 *
 * So resolve it here instead, against the ids that actually exist in this
 * document: whitespace-separated if it ever is, a single id when that matches,
 * otherwise consume the token greedily longest-first. Deterministic (ids are
 * unique in a document), and it degrades to the old single-id behaviour.
 */
function resolveTriggerIds(root, raw) {
    var token = String(raw || '').trim()
    if (token === '') return []
    if (/\s/.test(token)) return token.split(/\s+/).filter(Boolean)
    if (byId(root, token).length) return [token]

    var known = []
    Array.prototype.forEach.call(root.querySelectorAll('[id]'), function (el) {
        var id = el.getAttribute('id')
        if (id) known.push(id)
    })
    known.sort(function (a, b) { return b.length - a.length })

    var out = []
    var rest = token
    while (rest !== '') {
        var hit = null
        for (var i = 0; i < known.length; i++) {
            if (rest.indexOf(known[i]) === 0) { hit = known[i]; break }
        }
        if (hit === null) return out.length ? out : [token]
        out.push(hit)
        rest = rest.slice(hit.length)
    }

    return out
}

function applyPreviewTriggers(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return

    var selects = root.querySelectorAll('select')
    Array.prototype.forEach.call(selects, function (select) {
        var options = select.querySelectorAll('option[data-triggers]')
        Array.prototype.forEach.call(options, function (option) {
            var selected = option.value === select.value
            resolveTriggerIds(root, option.getAttribute('data-triggers')).forEach(function (id) {
                // Round 12: an inline shim is a sibling of its input, not the
                // element the trigger names, so reveal/hide it alongside or a
                // revealed clause would show its editor with no clause, or vice
                // versa.
                var targets = Array.prototype.slice.call(byId(root, id))
                    .concat(Array.prototype.slice.call(
                        root.querySelectorAll('[data-mc-shim-for="' + String(id).replace(/"/g, '\\"') + '"]')
                    ))
                targets.forEach(function (el) {
                    if (!el.style) return
                    if (selected) {
                        el.style.removeProperty('display')
                    } else {
                        el.style.setProperty('display', 'none', 'important')
                    }
                })
            })
        })
    })
}

/**
 * Round 11 (Joe req 2026-09-01): make the revealed fill-ins readable.
 *
 * components/policy-input.blade.php renders every `text` fill-in as
 * `class="form-control w-25 d-inline-block"`, and Bootstrap's .w-25 is
 * `width:25% !important`. Inside the preview pane that is about 119px, so a
 * reviewer saw "as necessary t" / "with the Cover" and had to scroll inside a
 * peephole to read the clause they were about to sign. Textareas are full
 * width but a fixed two rows, so the three individual-rights sentences were
 * clipped the same way.
 *
 * The server component is shared with company_policy/editor and
 * PolicyMedcurityInputs, so it is NOT changed. Sizing is applied here, and the
 * CSS that lets it take effect is confined to the two BAA preview panes
 * (.btb-doc in BaaTemplateBuilder.vue, .bgw-doc in BaaGuidedWizard.vue).
 *
 * previewInputSize() is separated out as a pure function so the growth rule is
 * unit-testable without a browser (tests/js/previewBinding.test.mjs).
 */
var PREVIEW_INPUT_MIN_CHARS = 14
var PREVIEW_INPUT_MAX_CHARS = 120

/**
 * The `size` (in characters) an inline fill-in should carry for its content.
 * Sized to the text plus one character of breathing room, floored so an empty
 * box is still an obvious, clickable field and capped so a very long answer
 * cannot push the paragraph wider than its pane (max-width:100% then governs).
 */
function previewInputSize(text) {
    var len = text === null || text === undefined ? 0 : String(text).length
    var wanted = len + 1
    if (wanted < PREVIEW_INPUT_MIN_CHARS) return PREVIEW_INPUT_MIN_CHARS
    if (wanted > PREVIEW_INPUT_MAX_CHARS) return PREVIEW_INPUT_MAX_CHARS

    return wanted
}

/**
 * Size every fill-in in a rendered preview to its own content: inline text
 * inputs grow in width, textareas grow in height to fit their wrapped text.
 *
 * Safe to call on every keystroke and after every reveal. A control that is
 * still hidden reports scrollHeight 0, so its height is left alone until
 * applyPreviewTriggers() reveals it and the next call sizes it properly - call
 * this AFTER applyPreviewTriggers, never before.
 */
function autoSizePreviewFields(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return

    Array.prototype.forEach.call(root.querySelectorAll('input'), function (el) {
        if (!el.getAttribute) return
        var type = (el.getAttribute('type') || 'text').toLowerCase()
        if (type !== 'text' && type !== 'tel') return
        var content = el.value
        if (content === null || content === undefined || content === '') {
            content = el.getAttribute('placeholder') || ''
        }
        el.setAttribute('size', String(previewInputSize(content)))
    })

    Array.prototype.forEach.call(root.querySelectorAll('textarea'), autoSizeTextarea)
}

/**
 * Grow one textarea to fit its own content, and shrink it back when text is
 * deleted. Split out of autoSizePreviewFields (Round 13) so it can also run on
 * the textarea's OWN input event: round 11 only sized these when a value
 * arrived from the question pane, so typing directly in the document clipped
 * (measured live: scrollHeight 372 against clientHeight 282).
 */
function autoSizeTextarea(el) {
    if (!el || !el.style) return
    // Reset first so the box can shrink again when text is deleted.
    el.style.height = 'auto'
    var needed = typeof el.scrollHeight === 'number' ? el.scrollHeight : 0
    if (needed > 0) {
        el.style.height = (needed + 2) + 'px'
    } else {
        el.style.removeProperty('height')
    }
}

/**
 * Round 10 (Joe req 2026-09-01): answers that are technically present but say
 * nothing. Dev's default template had permissible purposes saved as the literal
 * string "none", which then flowed into every BAA built from it, and round 9's
 * defaults only fill genuinely blank fields so it stayed.
 *
 * Deliberately conservative and matched BY VALUE, never by guessing intent:
 * only whitespace-only text and this exact literal list, case-insensitively,
 * after trimming. Anything else - including a real sentence that merely starts
 * with "None of ..." - is customer language and is left strictly alone.
 *
 * Mirrored in PHP by App\Services\Baa\StandardClauseDefaults::isJunkAnswer();
 * BaaTemplateFieldDefaultsTest pins the two lists against each other.
 */
var JUNK_ANSWERS = ['none', 'n/a', 'na', 'tbd', '-', '.']

function isJunkAnswer(value) {
    if (value === null || value === undefined) return true
    if (Array.isArray(value)) return value.length === 0
    var v = String(value).trim()
    if (v === '') return true

    return JUNK_ANSWERS.indexOf(v.toLowerCase()) !== -1
}

var DEPENDENT_FIELDS = {
    termination_date: { controller: 'termination_date_yes_no', showWhen: 'Yes' },
    ba_additional_specificity_access_yes: { controller: 'ba_additional_specificity_access', showWhen: 'Yes' },
    ba_additional_specificity_amendment_yes: { controller: 'ba_additional_specificity_amendment', showWhen: 'Yes' },
    ba_additional_specificity_accounting_yes: { controller: 'ba_additional_specificity_accounting', showWhen: 'Yes' },
    ba_how_use_phi_following_permissible_list: { controller: 'ba_how_use_phi', showWhen: 'Except as otherwise limited in this Agreement, the Parties agree that the following are permissible purposes:' },
    ba_how_wil_disclose_phi_following_requirements: { controller: 'ba_how_wil_disclose_phi', showWhen: 'Subject to the following minimum necessary requirements:' },
}

/**
 * Grouped themes for the standard-template builder's left column, in display
 * order. Slugs the standard Medcurity template uses today; any slug not
 * claimed by a theme lands in the trailing "Other details" group, so a future
 * template change degrades to "still shown", never "silently hidden".
 */
var THEMES = [
    { key: 'breach', title: 'Breach notification', slugs: ['stricter_timeframe', 'ba_handle_breach_notification'] },
    { key: 'rights', title: 'Individual rights requests', slugs: ['ba_additional_specificity_access', 'ba_additional_specificity_access_yes', 'ba_additional_specificity_amendment', 'ba_additional_specificity_amendment_yes', 'ba_additional_specificity_accounting', 'ba_additional_specificity_accounting_yes'] },
    { key: 'uses', title: 'Permitted uses & disclosures', slugs: ['ba_how_use_phi', 'ba_how_use_phi_following_permissible_list', 'ba_how_wil_disclose_phi', 'ba_how_wil_disclose_phi_following_requirements', 'ba_can_use_phi'] },
    { key: 'ce', title: 'Covered entity obligations', slugs: ['ba_provisions_for_ca', 'ba_permissible_requests'] },
    { key: 'term', title: 'Term & termination', slugs: ['termination_date_yes_no', 'termination_date', 'ba_add_termination', 'ba_destroy_phi', 'ba_authorized_phi', 'ba_transmit_to_ba', 'ba_add_terms'] },
]

var OTHER_THEME_TITLE = 'Other details'

/**
 * Fields answered per-vendor at send time, never at the template level: the
 * counterparty's name and the agreement dates. The builder shows them as
 * "asked when you send"; the streamlined send auto-fills them (vendor name,
 * today) and keeps the date editable at final review.
 */
var BA_NAME_SLUG = /^(business_associate|business_associate_name|ba_name|ba_legal_name|vendor_name|associate_legal_name)$/
var DATE_AUTO_SLUG = /^(as_of_date|effective_date|agreement_effective_date|date)$/
var PER_VENDOR_SLUG = /^(termination_date)$/

function isBaNameSlug(slug) { return BA_NAME_SLUG.test(String(slug || '')) }
function isDateAutoSlug(slug) { return DATE_AUTO_SLUG.test(String(slug || '')) }

/** Any field that is inherently per-send (not answerable once at the template level). */
function isVendorSpecificSlug(slug) {
    var s = String(slug || '')
    return BA_NAME_SLUG.test(s) || DATE_AUTO_SLUG.test(s) || PER_VENDOR_SLUG.test(s)
}

/**
 * Group an extracted field list (objects carrying at least {slug}) into the
 * builder's themed sections. Returns [{key, title, fields:[...]}] with empty
 * groups dropped and unclaimed fields in "Other details", preserving the
 * template's own field order inside each group.
 */
function groupFieldsByTheme(fields) {
    var list = Array.isArray(fields) ? fields : []
    var claimed = {}
    var groups = []
    THEMES.forEach(function (theme) {
        var inTheme = []
        theme.slugs.forEach(function (slug) { claimed[slug] = true })
        list.forEach(function (f) {
            if (theme.slugs.indexOf(f.slug) !== -1) inTheme.push(f)
        })
        if (inTheme.length) groups.push({ key: theme.key, title: theme.title, fields: inTheme })
    })
    var rest = list.filter(function (f) { return !claimed[f.slug] && !isVendorSpecificSlug(f.slug) })
    if (rest.length) groups.push({ key: 'other', title: OTHER_THEME_TITLE, fields: rest })
    return groups
}

export {
    FIELD_COPY,
    FIELD_PREFILL,
    prefillFor,
    resolveFieldValue,
    applyPreviewTriggers,
    resolveTriggerIds,
    autoSizePreviewFields,
    previewInputSize,
    mountInlineEditors,
    syncInlineEditors,
    bindPreviewEditing,
    applyPreviewReadOnly,
    mirrorFieldValue,
    readControlValue,
    normalizePlainText,
    autoSizeTextarea,
    needsInlineEditor,
    normalizeInlineText,
    inlineEditorAttrs,
    INLINE_WRAP_SLUGS,
    isJunkAnswer,
    JUNK_ANSWERS,
    DEPENDENT_FIELDS,
    THEMES,
    groupFieldsByTheme,
    isVendorSpecificSlug,
    isBaNameSlug,
    isDateAutoSlug,
}
